import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, ShieldCheck, ShieldOff, Smartphone, Copy, Check, Lock } from 'lucide-react'
import {
  getTotpAuthenticator,
  activateTotp,
  deleteTotp,
  reauthenticate,
  isReauthRequired,
} from '@/shared/lib/auth-api'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'

type SetupStep = 'idle' | 'setup' | 'done'

export function MfaTab() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [enrolled, setEnrolled] = useState(false)

  // Setup flow state
  const [setupStep, setSetupStep] = useState<SetupStep>('idle')
  const [secret, setSecret] = useState('')
  const [totpUrl, setTotpUrl] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [copied, setCopied] = useState(false)

  // Disable flow state
  const [disabling, setDisabling] = useState(false)
  const [confirmDisable, setConfirmDisable] = useState(false)

  // Reauthentication gate: allauth requires a recent password confirmation
  // before managing authenticators. When an action returns that flow we stash
  // it and replay it after the password is confirmed.
  const [reauthNeeded, setReauthNeeded] = useState(false)
  const [reauthPassword, setReauthPassword] = useState('')
  const [reauthError, setReauthError] = useState('')
  const [reauthing, setReauthing] = useState(false)
  const pendingAction = useRef<(() => Promise<void>) | null>(null)

  useEffect(() => {
    void loadStatus()
  }, [])

  async function loadStatus() {
    try {
      // 404 = not enrolled (carries meta.secret + meta.totp_url for setup);
      // 200 = a TOTP authenticator already exists.
      const res = await getTotpAuthenticator()
      if (res.status === 200) {
        setEnrolled(true)
      } else {
        setEnrolled(false)
        setSecret(res.meta?.secret ?? '')
        setTotpUrl((res.meta as { totp_url?: string } | undefined)?.totp_url ?? '')
      }
    } catch {
      setEnrolled(false)
    } finally {
      setLoading(false)
    }
  }

  function handleStartSetup() {
    setVerifyCode('')
    setSetupStep('setup')
  }

  async function attemptActivate() {
    const res = await activateTotp(verifyCode)
    if (res.status === 200) {
      setSetupStep('done')
      setEnrolled(true)
      toast.success('Two-factor authentication enabled')
    } else if (isReauthRequired(res)) {
      pendingAction.current = attemptActivate
      setReauthError('')
      setReauthNeeded(true)
    } else {
      toast.error(res.errors?.[0]?.message ?? 'Invalid code. Please try again.')
    }
  }

  async function handleVerifyCode() {
    if (verifyCode.length !== 6) return
    setVerifying(true)
    try {
      await attemptActivate()
    } catch {
      toast.error('Invalid code. Please try again.')
    } finally {
      setVerifying(false)
    }
  }

  async function attemptDisable() {
    const res = await deleteTotp()
    if (res.status === 200) {
      toast.success('Two-factor authentication disabled')
      setConfirmDisable(false)
      setSetupStep('idle')
      setVerifyCode('')
      await loadStatus()
    } else if (isReauthRequired(res)) {
      pendingAction.current = attemptDisable
      setReauthError('')
      setReauthNeeded(true)
    } else {
      toast.error(res.errors?.[0]?.message ?? 'Failed to disable two-factor authentication')
    }
  }

  async function handleDisable() {
    setDisabling(true)
    try {
      await attemptDisable()
    } catch {
      toast.error('Failed to disable two-factor authentication')
    } finally {
      setDisabling(false)
    }
  }

  async function handleReauth() {
    if (!reauthPassword) return
    setReauthing(true)
    setReauthError('')
    try {
      const res = await reauthenticate(reauthPassword)
      if (res.status === 200) {
        setReauthNeeded(false)
        setReauthPassword('')
        const action = pendingAction.current
        pendingAction.current = null
        if (action) await action()
      } else {
        setReauthError(res.errors?.[0]?.message ?? 'Incorrect password. Please try again.')
      }
    } catch {
      setReauthError('Incorrect password. Please try again.')
    } finally {
      setReauthing(false)
    }
  }

  function cancelReauth() {
    setReauthNeeded(false)
    setReauthPassword('')
    setReauthError('')
    pendingAction.current = null
  }

  function handleCopySecret() {
    navigator.clipboard.writeText(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function handleCancelSetup() {
    setSetupStep('idle')
    setVerifyCode('')
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* MFA Status */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          {t('mfa.title')}
        </h3>

        <div className="flex items-center gap-4">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              enrolled ? 'bg-[var(--status-ok-bg)]' : 'bg-[var(--bg-subtle)]'
            }`}
          >
            {enrolled ? (
              <ShieldCheck className="h-4 w-4 text-[var(--status-ok)]" />
            ) : (
              <ShieldOff className="h-4 w-4 text-[var(--ink-3)]" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-[13px] text-[var(--ink)]">
              {enrolled
                ? 'Two-factor authentication is enabled'
                : 'Two-factor authentication is not enabled'}
            </p>
            <p className="text-[11px] text-[var(--ink-3)]">
              {enrolled
                ? 'Your account is protected with an authenticator app.'
                : 'Add an authenticator app for an extra layer of security.'}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] ${
              enrolled
                ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
            }`}
          >
            {enrolled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
      {/* Setup / Disable Actions */}
      <div>
        <h3 className="clara-section-label mb-5 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          {enrolled ? 'Manage' : 'Setup'}
        </h3>

        {reauthNeeded ? (
          <div className="max-w-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
                <Lock className="h-4 w-4 text-[var(--ink-3)]" />
              </div>
              <div>
                <p className="text-[13px] text-[var(--ink)]">Confirm your password</p>
                <p className="text-[11px] text-[var(--ink-3)]">
                  For your security, re-enter your password to manage two-factor
                  authentication.
                </p>
              </div>
            </div>
            <div className="mb-4">
              <Label className="clara-section-label mb-1.5 block">Password</Label>
              <Input
                type="password"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleReauth()
                }}
                autoComplete="current-password"
                autoFocus
                placeholder="••••••••"
                className="text-[13px]"
              />
              {reauthError && (
                <p className="mt-1.5 text-[11px] text-[var(--status-error)]">{reauthError}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={cancelReauth}
                className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150"
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleReauth()}
                disabled={reauthing}
                className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-40"
              >
                {reauthing ? 'Confirming...' : 'Confirm'}
              </Button>
            </div>
          </div>
        ) : (
          <>
        {/* Not enrolled: show setup entry point */}
        {!enrolled && setupStep === 'idle' && (
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
              <Smartphone className="h-4 w-4 text-[var(--ink-3)]" />
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-[var(--ink)]">Authenticator app</p>
              <p className="text-[11px] text-[var(--ink-3)]">
                Use Google Authenticator, Authy, or any TOTP-compatible app.
              </p>
            </div>
            <Button
              data-testid="mfa-setup-btn"
              onClick={handleStartSetup}
              className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
            >
              Set up
            </Button>
          </div>
        )}

        {/* Setup step: manual secret + otpauth link (no QR dependency) */}
        {setupStep === 'setup' && (
          <div className="max-w-sm">
            <p className="mb-4 text-[13px] text-[var(--ink)]">
              Add this account to your authenticator app, then enter the 6-digit
              code to confirm.
            </p>

            <div className="mb-4">
              <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Manual entry key
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 select-all rounded-[4px] bg-[var(--bg-subtle)] px-3 py-1.5 font-mono text-[12px] break-all text-[var(--ink)]">
                  {secret}
                </code>
                <Button
                  variant="ghost"
                  onClick={handleCopySecret}
                  className="cursor-pointer rounded-[4px] p-1.5 text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            {totpUrl && (
              <div className="mb-4">
                <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  Or open in your authenticator
                </p>
                <a
                  href={totpUrl}
                  className="block break-all text-[11px] text-[var(--green)] underline"
                >
                  {totpUrl}
                </a>
              </div>
            )}

            <div className="mb-4">
              <Label className="clara-section-label mb-1.5 block">Verification code</Label>
              <Input
                data-testid="mfa-code-input"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center font-mono text-[13px] tracking-[0.3em]"
                maxLength={6}
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleCancelSetup}
                className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150"
              >
                Cancel
              </Button>
              <Button
                data-testid="mfa-verify-btn"
                onClick={handleVerifyCode}
                disabled={verifyCode.length !== 6 || verifying}
                className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-40"
              >
                {verifying ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </div>
          </div>
        )}

        {/* Setup complete */}
        {setupStep === 'done' && (
          <div className="flex items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--green)] text-[var(--bg)]">
              <Check className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[13px] text-[var(--ink)]">MFA is now active</p>
              <p className="text-[11px] text-[var(--ink-3)]">
                Your account is protected with two-factor authentication.
              </p>
            </div>
          </div>
        )}

        {/* Enrolled: show disable option */}
        {enrolled && setupStep !== 'done' && (
          <div>
            <div className="mb-4 flex items-center gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--green-bg)]">
                <Smartphone className="h-4 w-4 text-[var(--green)]" />
              </div>
              <div className="flex-1">
                <p className="text-[13px] text-[var(--ink)]">Authenticator app</p>
                <p className="text-[11px] text-[var(--ink-3)]">TOTP authenticator active</p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--status-ok-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--status-ok)]">
                <span className="inline-block h-[6px] w-[6px] rounded-full bg-[var(--status-ok)]" />
                Active
              </span>
            </div>

            {!confirmDisable ? (
              <Button
                variant="outline"
                onClick={() => setConfirmDisable(true)}
                className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--status-error)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--status-error)] transition-all duration-150"
              >
                Disable MFA
              </Button>
            ) : (
              <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                <p className="mb-3 text-[13px] text-[var(--ink)]">
                  Are you sure? This removes the authenticator from your account.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setConfirmDisable(false)}
                    className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDisable}
                    disabled={disabling}
                    className="cursor-pointer rounded-[4px] bg-[var(--status-error)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-40"
                  >
                    {disabling ? 'Disabling...' : 'Confirm disable'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  )
}
